import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Host, HostBinding, Input, Output, HostListener, ViewEncapsulation, ContentChildren, Directive, Optional, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$1 from '@angular/router';
import { NavigationEnd, RouterLink, RouterLinkWithHref, RouterModule } from '@angular/router';
import { ReplaySubject, Subject, merge, timer, of, from } from 'rxjs';
import { startWith, takeUntil, map, switchMap, filter, delay, take, mergeAll } from 'rxjs/operators';

class TabComponent {
    constructor(hostRef, changeDetectorRef) {
        this.hostRef = hostRef;
        this.changeDetectorRef = changeDetectorRef;
        /** @internal */
        this.tabItemClass = true;
        /** @internal */
        this.isActive = false;
        /** @internal */
        this.activeBar = false;
        this.activeChange = new EventEmitter();
        /** @internal */
        this.activeStateChange$ = new ReplaySubject();
        this.disabled = null;
        /** @internal */
        this.roleAttr = 'tab';
        /** @internal */
        this.typeAttr = 'button';
    }
    /** @internal */
    get tabindexAttr() {
        return this.disabled ? -1 : null;
    }
    get isDisabledAttr() {
        return this.disabled ? '' : null;
    }
    /** @internal */
    get fallbackTitle() {
        var _a, _b;
        return (_b = (_a = this.hostRef.nativeElement) === null || _a === void 0 ? void 0 : _a.innerText) === null || _b === void 0 ? void 0 : _b.trim();
    }
    ngOnInit() {
        this.activeBar = this.isActive;
    }
    /** @internal */
    onClick() {
        if (!this.isActive) {
            this.isActive = true;
            this.activeStateChange$.next(this.isActive);
        }
    }
    /** @internal */
    updateActive(active) {
        this.isActive = active;
        this.activeChange.emit(this.isActive);
        this.activeStateChange$.next(this.isActive);
        if (!this.isActive) {
            this.activeBar = false;
        }
        this.changeDetectorRef.markForCheck();
    }
    /** @internal */
    updateActiveBar() {
        this.activeBar = this.isActive;
        this.changeDetectorRef.markForCheck();
    }
}
TabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabComponent, deps: [{ token: i0.ElementRef, host: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
TabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: TabComponent, selector: "sd-tab", inputs: { title: "title", disabled: "disabled" }, outputs: { activeChange: "activeChange" }, host: { listeners: { "click": "onClick()" }, properties: { "class.tab__item": "this.tabItemClass", "class.is-active": "this.isActive", "class.is-active-bar": "this.activeBar", "class.is-disabled": "this.disabled", "attr.role": "this.roleAttr", "attr.type": "this.typeAttr", "attr.tabindex": "this.tabindexAttr", "attr.is-disabled": "this.isDisabledAttr" } }, exportAs: ["sdTab"], ngImport: i0, template: "<ng-content select=\"[sdUiIcon], sd-ui-icon\"></ng-content>\n<div class=\"tab__text\" [title]=\"title || fallbackTitle\"><ng-content></ng-content></div>\n<div class=\"tab__indicator-static\"></div>\n", styles: [":host{-webkit-appearance:none!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sd-tab', changeDetection: ChangeDetectionStrategy.OnPush, exportAs: 'sdTab', template: "<ng-content select=\"[sdUiIcon], sd-ui-icon\"></ng-content>\n<div class=\"tab__text\" [title]=\"title || fallbackTitle\"><ng-content></ng-content></div>\n<div class=\"tab__indicator-static\"></div>\n", styles: [":host{-webkit-appearance:none!important}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: i0.ChangeDetectorRef }];
    }, propDecorators: { tabItemClass: [{
                type: HostBinding,
                args: ['class.tab__item']
            }], title: [{
                type: Input
            }], isActive: [{
                type: HostBinding,
                args: ['class.is-active']
            }], activeBar: [{
                type: HostBinding,
                args: ['class.is-active-bar']
            }], activeChange: [{
                type: Output
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.is-disabled']
            }], roleAttr: [{
                type: HostBinding,
                args: ['attr.role']
            }], typeAttr: [{
                type: HostBinding,
                args: ['attr.type']
            }], tabindexAttr: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], isDisabledAttr: [{
                type: HostBinding,
                args: ['attr.is-disabled']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class TabsComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.size = 100;
        this.variant = 'fixed';
        this.onDark = false;
        this.destroyed$ = new Subject();
    }
    /** @internal */
    get classes() {
        return {
            tab: true,
            'tab--size-100': this.size === 100,
            'tab--size-200': this.size === 200,
            'tab--fixed': this.variant === 'fixed',
            'tab--percentage': this.variant === 'percentage',
            'tab--dark': this.onDark,
        };
    }
    ngAfterViewInit() {
        const tabs$ = this.tabRefs.changes.pipe(
        // start with initial event
        startWith(this.tabRefs), 
        // auto unsubscribe
        takeUntil(this.destroyed$));
        tabs$
            .pipe(
        // map to array of activeStateChange events
        map((tabs) => tabs.map((tab) => tab.activeStateChange$.pipe(map((state) => [tab, state])))), 
        // merge all observables to one single
        switchMap((events) => merge(...events)), 
        // we do only care for active tabs rom here
        filter(([tab, state]) => tab && state && tab.isActive), delay(1))
            .subscribe(([tab]) => {
            const active = this.tabRefs.filter((t) => t.isActive && t !== tab);
            const previousActive = active.length > 0 ? active[0] : this.previousActiveTabRef;
            if (previousActive && !active.includes(previousActive)) {
                active.push(previousActive);
            }
            if (tab === previousActive) {
                return;
            }
            if (previousActive) {
                active.forEach((t) => {
                    delayExec(() => t.updateActive(false));
                });
                this.animateIndicator(previousActive, tab);
            }
            else {
                tab.updateActiveBar();
            }
            this.previousActiveTabRef = tab;
        });
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
    animateIndicator(from, to) {
        delayExec(() => {
            var _a, _b, _c, _d, _e;
            this.indicatorStyles = {
                transitionDuration: '0s',
                webkitTransitionDuration: '0s',
                width: `${(_c = (_b = (_a = from === null || from === void 0 ? void 0 : from.hostRef) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.offsetWidth) !== null && _c !== void 0 ? _c : 0}px`,
                left: `${((_e = (_d = from === null || from === void 0 ? void 0 : from.hostRef) === null || _d === void 0 ? void 0 : _d.nativeElement) === null || _e === void 0 ? void 0 : _e.offsetLeft) || 0}px`,
                opacity: 1,
            };
            this.changeDetectorRef.markForCheck();
            // call timeout async to allow browser to update the previously reset styles (browser reflow)
            delayExec(() => {
                this.indicatorStyles = {
                    transitionDuration: '0.15s',
                    webkitTransitionDuration: '0.15s',
                    width: `${to.hostRef.nativeElement.offsetWidth}px`,
                    left: `${to.hostRef.nativeElement.offsetLeft}px`,
                    opacity: 1,
                };
                this.changeDetectorRef.markForCheck();
            }, 100);
        });
    }
    /** @internal */
    onAnimatedIndicatorAnimationEnd() {
        var _a;
        (_a = this.previousActiveTabRef) === null || _a === void 0 ? void 0 : _a.updateActiveBar();
        this.indicatorStyles = {
            transitionDuration: '0s',
            webkitTransitionDuration: '0s',
            opacity: 0,
        };
        this.changeDetectorRef.markForCheck();
    }
}
TabsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabsComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
TabsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: TabsComponent, selector: "sd-tabs", inputs: { size: "size", variant: "variant", onDark: "onDark" }, host: { properties: { "class": "this.classes" } }, queries: [{ propertyName: "tabRefs", predicate: TabComponent }], exportAs: ["sdTabs"], ngImport: i0, template: "<div class=\"tab__container\">\n  <div class=\"tab__items\">\n    <ng-content select=\"sd-tab\"></ng-content>\n  </div>\n  <div class=\"tab__indicator-animated\" [ngStyle]=\"indicatorStyles\" (transitionend)=\"onAnimatedIndicatorAnimationEnd()\"></div>\n  <div class=\"tab__border-full\"></div>\n</div>\n", styles: ["@charset \"UTF-8\";.tab{display:inline-flex;max-width:100%;-webkit-overflow-scrolling:touch;overflow-y:scroll;transition-property:color;scrollbar-width:none;-ms-overflow-style:none}.tab::-webkit-scrollbar{display:none}.tab--fixed{box-sizing:content-box;box-sizing:initial;padding-right:.75rem}@media screen and (max-width: 767px){.tab--percentage{display:flex}}.tab--size-100 .tab__item{height:3.5rem;margin:0 1rem}.tab--size-200 .tab__item{height:5rem;margin:0 1.25rem}.tab__container{position:relative;display:flex;flex:1;flex-basis:auto;flex-direction:row;flex-shrink:0}.tab__items{display:flex;flex:1;flex-basis:auto;flex-shrink:0}.tab__item{position:relative;box-sizing:border-box;display:flex;align-items:center;height:3.5rem;margin:0 1rem;font:inherit;font-size:1rem;color:var(--color-grey-700);white-space:nowrap;cursor:pointer;transition-timing-function:ease;transition-duration:.15s;transition-property:color}.tab--dark .tab__item{color:var(--color-grey-100)}@media screen and (max-width: 767px){.tab--percentage .tab__item{flex:1;flex-shrink:0;justify-content:center;margin:0}}.tab--percentage .tab__item:last-child{margin-right:0}.tab__item:first-child{margin-left:0}.tab__item:focus{outline-offset:-3px}.tab__item:hover{color:var(--color-grey-500)}.tab--dark .tab__item:hover{color:var(--color-grey-300)}.tab__item:active{color:var(--color-grey-400)}.tab__item[disabled]{color:var(--color-grey-700);opacity:.25}.tab--dark .tab__item[disabled]{color:var(--color-grey-100)}.tab__item.is-active{font-weight:700;color:var(--color-grey-700)}.tab--dark .tab__item.is-active{color:var(--color-white)}.tab__item.is-active-bar .tab__indicator-static{position:absolute;bottom:0;width:100%;height:2px;background:var(--color-primary-300);border-radius:1px 1px 0 0;left:0;z-index:10}.tab__icon{display:inline-block;pointer-events:none}.tab--dark .tab__icon path{fill:var(--color-white)}.tab__text{display:inline-block;text-align:left;letter-spacing:0}.tab__text:after{display:block;height:0;overflow:hidden;font-weight:700;text-align:left;visibility:hidden;content:attr(title)}.tab--dark .tab__text{color:var(--color-white)}.tab__item.is-active .tab__text{font-weight:700;text-align:left}.tab__icon+.tab__text{margin-left:10px}.tab__indicator-animated{position:absolute;bottom:0;width:100%;height:2px;background:var(--color-primary-300);border-radius:1px 1px 0 0;z-index:10;width:0;transition:.3s;transition-property:all}.tab__border-full{position:absolute;bottom:0;left:0;display:block;height:1px;background:var(--color-grey-200)}.tab--dark .tab__border-full{background:var(--color-grey-500)}.tab--fixed.tab--size-100 .tab__border-full{width:calc(100% - 1rem)}.tab--fixed.tab--size-200 .tab__border-full{width:calc(100% - 1.25rem)}.tab--percentage.tab--size-100 .tab__border-full,.tab--percentage.tab--size-200 .tab__border-full{width:100%}\n"], directives: [{ type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sd-tabs', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, exportAs: 'sdTabs', template: "<div class=\"tab__container\">\n  <div class=\"tab__items\">\n    <ng-content select=\"sd-tab\"></ng-content>\n  </div>\n  <div class=\"tab__indicator-animated\" [ngStyle]=\"indicatorStyles\" (transitionend)=\"onAnimatedIndicatorAnimationEnd()\"></div>\n  <div class=\"tab__border-full\"></div>\n</div>\n", styles: ["@charset \"UTF-8\";.tab{display:inline-flex;max-width:100%;-webkit-overflow-scrolling:touch;overflow-y:scroll;transition-property:color;scrollbar-width:none;-ms-overflow-style:none}.tab::-webkit-scrollbar{display:none}.tab--fixed{box-sizing:content-box;box-sizing:initial;padding-right:.75rem}@media screen and (max-width: 767px){.tab--percentage{display:flex}}.tab--size-100 .tab__item{height:3.5rem;margin:0 1rem}.tab--size-200 .tab__item{height:5rem;margin:0 1.25rem}.tab__container{position:relative;display:flex;flex:1;flex-basis:auto;flex-direction:row;flex-shrink:0}.tab__items{display:flex;flex:1;flex-basis:auto;flex-shrink:0}.tab__item{position:relative;box-sizing:border-box;display:flex;align-items:center;height:3.5rem;margin:0 1rem;font:inherit;font-size:1rem;color:var(--color-grey-700);white-space:nowrap;cursor:pointer;transition-timing-function:ease;transition-duration:.15s;transition-property:color}.tab--dark .tab__item{color:var(--color-grey-100)}@media screen and (max-width: 767px){.tab--percentage .tab__item{flex:1;flex-shrink:0;justify-content:center;margin:0}}.tab--percentage .tab__item:last-child{margin-right:0}.tab__item:first-child{margin-left:0}.tab__item:focus{outline-offset:-3px}.tab__item:hover{color:var(--color-grey-500)}.tab--dark .tab__item:hover{color:var(--color-grey-300)}.tab__item:active{color:var(--color-grey-400)}.tab__item[disabled]{color:var(--color-grey-700);opacity:.25}.tab--dark .tab__item[disabled]{color:var(--color-grey-100)}.tab__item.is-active{font-weight:700;color:var(--color-grey-700)}.tab--dark .tab__item.is-active{color:var(--color-white)}.tab__item.is-active-bar .tab__indicator-static{position:absolute;bottom:0;width:100%;height:2px;background:var(--color-primary-300);border-radius:1px 1px 0 0;left:0;z-index:10}.tab__icon{display:inline-block;pointer-events:none}.tab--dark .tab__icon path{fill:var(--color-white)}.tab__text{display:inline-block;text-align:left;letter-spacing:0}.tab__text:after{display:block;height:0;overflow:hidden;font-weight:700;text-align:left;visibility:hidden;content:attr(title)}.tab--dark .tab__text{color:var(--color-white)}.tab__item.is-active .tab__text{font-weight:700;text-align:left}.tab__icon+.tab__text{margin-left:10px}.tab__indicator-animated{position:absolute;bottom:0;width:100%;height:2px;background:var(--color-primary-300);border-radius:1px 1px 0 0;z-index:10;width:0;transition:.3s;transition-property:all}.tab__border-full{position:absolute;bottom:0;left:0;display:block;height:1px;background:var(--color-grey-200)}.tab--dark .tab__border-full{background:var(--color-grey-500)}.tab--fixed.tab--size-100 .tab__border-full{width:calc(100% - 1rem)}.tab--fixed.tab--size-200 .tab__border-full{width:calc(100% - 1.25rem)}.tab--percentage.tab--size-100 .tab__border-full,.tab--percentage.tab--size-200 .tab__border-full{width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { classes: [{
                type: HostBinding,
                args: ['class']
            }], size: [{
                type: Input
            }], variant: [{
                type: Input
            }], onDark: [{
                type: Input
            }], tabRefs: [{
                type: ContentChildren,
                args: [TabComponent]
            }] } });
const delayExec = (cb, time = 1) => timer(time).pipe(take(1)).subscribe(cb);

class TabWithActiveDirective {
    constructor(tab) {
        this.tab = tab;
        this.active = false;
    }
    ngOnInit() {
        this.tab.isActive = this.active;
        this.tab.activeBar = this.active;
    }
    ngOnChanges(changes) {
        if ('active' in changes && changes.active.currentValue !== changes.active.previousValue) {
            this.tab.updateActive(changes.active.currentValue);
        }
    }
}
TabWithActiveDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabWithActiveDirective, deps: [{ token: TabComponent, host: true }], target: i0.ɵɵFactoryTarget.Directive });
TabWithActiveDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: TabWithActiveDirective, selector: "sd-tab:not([routerLink])", inputs: { active: "active" }, exportAs: ["tabDirective"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabWithActiveDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'sd-tab:not([routerLink])',
                    exportAs: 'tabDirective',
                }]
        }], ctorParameters: function () {
        return [{ type: TabComponent, decorators: [{
                        type: Host
                    }] }];
    }, propDecorators: { active: [{
                type: Input
            }] } });

class TabWithRouterLinkDirective {
    constructor(router, tab, link, linkWithHref) {
        this.router = router;
        this.tab = tab;
        this.link = link;
        this.linkWithHref = linkWithHref;
        this.defaultRouterMatchingOptions = {
            paths: 'exact',
            queryParams: 'exact',
            fragment: 'ignored',
            matrixParams: 'ignored',
        };
        /**
         * Options to configure how to determine if the router link is active.
         *
         * These options are passed to the `Router.isActive()` function.
         *
         * @see Router.isActive
         */
        this.routerMatchingOptions = this.defaultRouterMatchingOptions;
        this.routerEventsSubscription = router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe(() => this.update());
    }
    ngAfterContentInit() {
        // `of(null)` is used to force subscribe body to execute once immediately (like `startWith`).
        of(this.links.changes, this.linksWithHrefs.changes, of(null))
            .pipe(mergeAll())
            .subscribe((_) => {
            this.update();
            this.subscribeToEachLinkOnChanges();
        });
    }
    subscribeToEachLinkOnChanges() {
        var _a;
        (_a = this.linkInputChangesSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        const allLinkChanges = [...this.links.toArray(), ...this.linksWithHrefs.toArray(), this.link, this.linkWithHref]
            .filter((link) => !!link)
            .map((link) => {
            return link;
        })
            .filter((link) => 'onChanges' in link)
            .map((link) => link.onChanges)
            .filter((onChanges) => !!onChanges);
        this.linkInputChangesSubscription = from(allLinkChanges)
            .pipe(mergeAll())
            .subscribe((link) => {
            if (this.isActive !== this.isLinkActive(this.router)(link)) {
                this.update();
            }
        });
    }
    /** @nodoc */
    ngOnChanges(changes) {
        this.update();
    }
    /** @nodoc */
    ngOnDestroy() {
        var _a;
        this.routerEventsSubscription.unsubscribe();
        (_a = this.linkInputChangesSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
    }
    update() {
        if (!this.links || !this.linksWithHrefs || !this.router.navigated) {
            return;
        }
        Promise.resolve().then(() => {
            const hasActiveLinks = this.hasActiveLinks();
            const isInitialChange = this.isActive === undefined;
            if (this.isActive !== hasActiveLinks) {
                this.isActive = hasActiveLinks;
                if (this.isActive) {
                    this.tab.updateActive(this.isActive);
                    if (isInitialChange && this.isActive) {
                        this.tab.updateActiveBar();
                    }
                }
            }
        });
    }
    isLinkActive(router) {
        let options = this.defaultRouterMatchingOptions;
        if (typeof this.routerMatchingOptions === 'object') {
            options = Object.assign(Object.assign({}, options), this.routerMatchingOptions);
        }
        return (link) => router.isActive(link.urlTree, options);
    }
    hasActiveLinks() {
        const isActiveCheckFn = this.isLinkActive(this.router);
        return ((this.link && isActiveCheckFn(this.link)) ||
            (this.linkWithHref && isActiveCheckFn(this.linkWithHref)) ||
            this.links.some(isActiveCheckFn) ||
            this.linksWithHrefs.some(isActiveCheckFn));
    }
}
TabWithRouterLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabWithRouterLinkDirective, deps: [{ token: i1$1.Router }, { token: TabComponent, host: true }, { token: i1$1.RouterLink, optional: true }, { token: i1$1.RouterLinkWithHref, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
TabWithRouterLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: TabWithRouterLinkDirective, selector: "sd-tab[routerLink]", inputs: { routerMatchingOptions: "routerMatchingOptions" }, queries: [{ propertyName: "links", predicate: RouterLink, descendants: true }, { propertyName: "linksWithHrefs", predicate: RouterLinkWithHref, descendants: true }], exportAs: ["tabDirective"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TabWithRouterLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'sd-tab[routerLink]',
                    exportAs: 'tabDirective',
                }]
        }], ctorParameters: function () {
        return [{ type: i1$1.Router }, { type: TabComponent, decorators: [{
                        type: Host
                    }] }, { type: i1$1.RouterLink, decorators: [{
                        type: Optional
                    }] }, { type: i1$1.RouterLinkWithHref, decorators: [{
                        type: Optional
                    }] }];
    }, propDecorators: { links: [{
                type: ContentChildren,
                args: [RouterLink, { descendants: true }]
            }], linksWithHrefs: [{
                type: ContentChildren,
                args: [RouterLinkWithHref, { descendants: true }]
            }], routerMatchingOptions: [{
                type: Input
            }] } });

class SmartDesignTabModule {
}
SmartDesignTabModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignTabModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SmartDesignTabModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignTabModule, declarations: [TabsComponent, TabComponent, TabWithActiveDirective, TabWithRouterLinkDirective], imports: [CommonModule, RouterModule], exports: [TabsComponent, TabComponent, TabWithActiveDirective, TabWithRouterLinkDirective] });
SmartDesignTabModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignTabModule, imports: [[CommonModule, RouterModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignTabModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TabsComponent, TabComponent, TabWithActiveDirective, TabWithRouterLinkDirective],
                    imports: [CommonModule, RouterModule],
                    exports: [TabsComponent, TabComponent, TabWithActiveDirective, TabWithRouterLinkDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SmartDesignTabModule, TabComponent, TabWithActiveDirective, TabWithRouterLinkDirective, TabsComponent };
